import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Ban, CalendarDays, CalendarPlus, Check, Download, Euro, Loader2,
  LogOut, Pencil, Phone, Save, Trash2, Users, X,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, API } from "@/api";

const STATUS = {
  pending: { label: "In attesa", cls: "bg-gold/15 text-gold border-gold/40" },
  confirmed: { label: "Confermata", cls: "bg-teal/15 text-teal-light border-teal/40" },
  paid: { label: "Pagata", cls: "bg-green-500/15 text-green-400 border-green-500/40" },
  cancelled: { label: "Annullata", cls: "bg-terracotta/15 text-terracotta border-terracotta/40" },
};

const FILTERS = ["", "pending", "confirmed", "paid", "cancelled"];
const FILTER_LABELS = { "": "Tutte", pending: "In attesa", confirmed: "Confermate", paid: "Pagate", cancelled: "Annullate" };

export default function Admin() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [services, setServices] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("");
  const [slotService, setSlotService] = useState("tour_short_2h");
  const [slotMonth, setSlotMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [slots, setSlots] = useState([]);
  const [newSlot, setNewSlot] = useState({ date: "", time: "09:00", capacity: 8 });
  const [editingSlot, setEditingSlot] = useState(null);
  const [editForm, setEditForm] = useState({ time: "", capacity: 8 });
  const [bulk, setBulk] = useState({ from: "", to: "", capacity: 8, times: ["09:00", "14:30"] });
  const [editingBooking, setEditingBooking] = useState(null);
  const [editB, setEditB] = useState(null);
  const [editSlotOptions, setEditSlotOptions] = useState([]);
  const ALL_TIMES = ["09:00", "09:30", "11:00", "14:30", "15:00", "17:00", "18:30"];

  const svcTitle = (id) => services.find((s) => s.id === id)?.title_it || id;

  const loadBookings = async (f = filter) => {
    const { data } = await api.get(`/admin/bookings${f ? `?status=${f}` : ""}`);
    setBookings(data);
  };
  const loadStats = async () => {
    const { data } = await api.get("/admin/stats");
    setStats(data);
  };
  const loadSlots = async (sid = slotService, m = slotMonth) => {
    const { data } = await api.get(`/admin/slots?service_id=${sid}&month=${m}`);
    setSlots(data);
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        const svcs = await api.get("/services");
        setServices(svcs.data);
        await Promise.all([loadBookings(""), loadStats(), loadSlots()]);
      } catch {
        navigate("/admin/login");
      } finally {
        setChecking(false);
      }
    })();
    // eslint-disable-next-line
  }, []);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { status });
      await Promise.all([loadBookings(), loadStats()]);
      toast.success(`Prenotazione ${STATUS[status].label.toLowerCase()}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleSlot = async (id, active) => {
    try {
      await api.patch(`/admin/slots/${id}`, { active });
      await loadSlots();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const addSlot = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/slots", { service_id: slotService, ...newSlot, capacity: Number(newSlot.capacity) });
      setNewSlot({ date: "", time: "09:00", capacity: 8 });
      await loadSlots();
      toast.success("Slot creato");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleDay = async (date, active) => {
    try {
      await api.post("/admin/slots/day", { service_id: slotService, date, active });
      await loadSlots();
      toast.success(active ? "Giornata riattivata" : "Giornata bloccata");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const saveSlotEdit = async (id) => {
    try {
      await api.patch(`/admin/slots/${id}`, { time: editForm.time, capacity: Number(editForm.capacity) });
      setEditingSlot(null);
      await loadSlots();
      toast.success("Slot aggiornato");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteSlot = async (id) => {
    try {
      await api.delete(`/admin/slots/${id}`);
      setEditingSlot(null);
      await loadSlots();
      toast.success("Slot eliminato");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const bulkCreate = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/slots/bulk", {
        service_id: slotService,
        from_date: bulk.from,
        to_date: bulk.to,
        times: bulk.times,
        capacity: Number(bulk.capacity),
      });
      await loadSlots();
      toast.success(`${data.created} slot creati · ${data.skipped} già esistenti`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openEditBooking = async (b) => {
    setEditingBooking(b);
    setEditB({
      name: b.name,
      email: b.email,
      phone: b.phone,
      notes: b.notes || "",
      participants: b.participants,
      is_resident: !!b.is_resident,
      hours: b.hours || 1,
      slot_id: b.slot_id,
    });
    const months = [0, 1, 2].map((i) => {
      const d = new Date();
      d.setDate(1);
      d.setMonth(d.getMonth() + i);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    });
    const results = await Promise.all(
      months.map((m) => api.get(`/availability?service_id=${b.service_id}&month=${m}`).then((r) => r.data).catch(() => ({})))
    );
    const opts = [];
    results.forEach((av) => {
      Object.entries(av).forEach(([date, daySlots]) => {
        daySlots.forEach((s) => {
          if (s.remaining >= b.participants) {
            const label = `${new Date(date + "T12:00:00").toLocaleDateString("it-IT", { weekday: "short", day: "numeric", month: "short" })} · ${s.time} — ${s.remaining} posti liberi`;
            opts.push({ slot_id: s.slot_id, label });
          }
        });
      });
    });
    setEditSlotOptions(opts);
  };

  const saveBookingEdit = async () => {
    try {
      await api.patch(`/admin/bookings/${editingBooking.id}`, {
        name: editB.name,
        email: editB.email,
        phone: editB.phone,
        notes: editB.notes,
        participants: Number(editB.participants),
        is_resident: editB.is_resident,
        hours: Number(editB.hours),
        slot_id: editB.slot_id !== editingBooking.slot_id ? editB.slot_id : null,
      });
      setEditingBooking(null);
      await Promise.all([loadBookings(), loadStats()]);
      toast.success("Prenotazione aggiornata, prezzo ricalcolato");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    navigate("/admin/login");
  };

  const shiftMonth = (delta) => {
    const [y, m] = slotMonth.split("-").map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    const nm = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    setSlotMonth(nm);
    loadSlots(slotService, nm);
  };

  const groupedSlots = useMemo(() => {
    const g = {};
    for (const s of slots) (g[s.date] = g[s.date] || []).push(s);
    return g;
  }, [slots]);

  if (checking) {
    return (
      <div className="min-h-screen bg-lake-deep flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-gold animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-lake-deep text-glacier grain relative">
      <header className="border-b hairline-light">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/guidone.png" alt="Guidone Circolo Vela Manerba" className="h-9 w-auto" data-testid="admin-logo-img" />
            <span className="font-serif text-lg">Dashboard Staff</span>
            <span className="hidden sm:inline font-mono text-[10px] text-glacier/40 ml-2">{user?.email}</span>
          </div>
          <div className="flex items-center gap-3">
            <a
              data-testid="export-csv-btn"
              href={`${API}/admin/bookings.csv`}
              className="hidden sm:flex items-center gap-2 border hairline-light rounded-full px-4 py-2 text-xs hover:bg-glacier/10 transition-colors"
            >
              <Download className="w-3.5 h-3.5" /> CSV
            </a>
            <Link to="/qr" data-testid="admin-qr-link" className="text-xs text-glacier/50 hover:text-gold transition-colors">Locandina QR</Link>
            <Link to="/" className="text-xs text-glacier/50 hover:text-glacier transition-colors">Sito</Link>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="flex items-center gap-2 border hairline-light rounded-full px-4 py-2 text-xs hover:bg-terracotta/20 transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" /> Esci
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            {[
              { icon: CalendarDays, label: "Uscite di oggi", value: stats.today, testid: "stat-today" },
              { icon: Users, label: "In attesa di conferma", value: stats.pending, testid: "stat-pending" },
              { icon: Euro, label: "Incasso stimato al circolo", value: `€${stats.revenue}`, testid: "stat-revenue" },
              { icon: Check, label: "Prenotazioni residenti", value: stats.residents, testid: "stat-residents" },
            ].map((s) => (
              <div key={s.testid} data-testid={s.testid} className="bg-lake-subtle border hairline-light rounded-md p-5">
                <s.icon className="w-5 h-5 text-gold" />
                <div className="mt-3 font-mono text-2xl font-semibold">{s.value}</div>
                <div className="mt-1 text-xs text-glacier/50">{s.label}</div>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-2 mb-8">
          {[["bookings", "Prenotazioni"], ["slots", "Calendario & Slot"]].map(([id, label]) => (
            <button
              key={id}
              data-testid={`admin-tab-${id}`}
              onClick={() => setTab(id)}
              className={`px-5 py-2.5 rounded-full text-sm font-medium border transition-colors ${
                tab === id ? "bg-gold text-lake-deep border-gold" : "hairline-light text-glacier/70 hover:text-glacier"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "bookings" && (
          <section>
            <div className="flex gap-2 flex-wrap mb-6">
              {FILTERS.map((f) => (
                <button
                  key={f || "all"}
                  data-testid={`filter-${f || "all"}`}
                  onClick={() => { setFilter(f); loadBookings(f); }}
                  className={`px-4 py-2 rounded-full text-xs font-mono tracking-wide border transition-colors ${
                    filter === f ? "bg-glacier text-lake-deep border-glacier" : "hairline-light text-glacier/60 hover:text-glacier"
                  }`}
                >
                  {FILTER_LABELS[f]}
                </button>
              ))}
            </div>

            {bookings.length === 0 ? (
              <p data-testid="no-bookings-msg" className="text-glacier/50 font-mono text-sm py-16 text-center">
                Nessuna prenotazione in questa categoria.
              </p>
            ) : (
              <div className="space-y-3">
                {bookings.map((b) => (
                  <article
                    key={b.id}
                    data-testid={`booking-row-${b.code}`}
                    className="bg-lake-subtle border hairline-light rounded-md p-5 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-5"
                  >
                    <div>
                      <div className="flex flex-wrap items-center gap-3">
                        <span className="font-mono text-gold font-semibold">{b.code}</span>
                        <span className={`text-[10px] font-mono uppercase tracking-[0.15em] border rounded-full px-2.5 py-1 ${STATUS[b.status].cls}`}>
                          {STATUS[b.status].label}
                        </span>
                        {b.is_resident === true || b.is_resident === "si" ? (
                          <span className="text-[10px] font-mono uppercase tracking-[0.15em] border border-gold/40 text-gold rounded-full px-2.5 py-1">
                            Residente −20%
                          </span>
                        ) : null}
                      </div>
                      <h3 className="mt-2 font-serif text-xl">{svcTitle(b.service_id)}</h3>
                      <p className="mt-1 text-sm text-glacier/60 capitalize">
                        {new Date(b.date + "T12:00:00").toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}
                        {" · "}{b.time} · {b.participants} {b.hours ? `kayak · ${b.hours}h` : "partecipanti"} ·{" "}
                        <span className="font-mono text-gold">€{b.price.total}</span>
                      </p>
                      <p className="mt-2 text-sm text-glacier/75 flex flex-wrap items-center gap-x-4 gap-y-1">
                        <span>{b.name}</span>
                        <span className="font-mono text-xs text-glacier/50">{b.email}</span>
                        <a href={`https://wa.me/${b.phone.replace(/[^0-9]/g, "")}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 font-mono text-xs text-teal-light hover:text-gold transition-colors">
                          <Phone className="w-3 h-3" /> {b.phone}
                        </a>
                      </p>
                      {b.notes && <p className="mt-2 text-xs text-glacier/50 italic">“{b.notes}”</p>}
                    </div>
                    <div className="flex lg:flex-col gap-2 lg:w-44">
                      <button
                        data-testid={`edit-${b.code}`}
                        onClick={() => openEditBooking(b)}
                        className="flex-1 border hairline-light text-glacier/70 rounded-full px-4 py-2 text-xs hover:bg-glacier/10 hover:text-glacier transition-colors flex items-center justify-center gap-1.5"
                      >
                        <Pencil className="w-3 h-3" /> Modifica
                      </button>
                      {b.status !== "confirmed" && b.status !== "cancelled" && (
                        <button
                          data-testid={`confirm-${b.code}`}
                          onClick={() => setStatus(b.id, "confirmed")}
                          className="flex-1 border border-teal/50 text-teal-light rounded-full px-4 py-2 text-xs hover:bg-teal hover:text-white transition-colors"
                        >
                          Conferma
                        </button>
                      )}
                      {b.status !== "paid" && b.status !== "cancelled" && (
                        <button
                          data-testid={`paid-${b.code}`}
                          onClick={() => setStatus(b.id, "paid")}
                          className="flex-1 border border-green-500/50 text-green-400 rounded-full px-4 py-2 text-xs hover:bg-green-600 hover:text-white transition-colors"
                        >
                          Pagata al circolo
                        </button>
                      )}
                      {b.status !== "cancelled" && (
                        <button
                          data-testid={`cancel-${b.code}`}
                          onClick={() => setStatus(b.id, "cancelled")}
                          className="flex-1 border border-terracotta/50 text-terracotta rounded-full px-4 py-2 text-xs hover:bg-terracotta hover:text-white transition-colors flex items-center justify-center gap-1.5"
                        >
                          <X className="w-3 h-3" /> Annulla
                        </button>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        )}

        {tab === "slots" && (
          <section>
            <div className="flex flex-wrap items-center gap-4 mb-6">
              <select
                data-testid="slot-service-select"
                value={slotService}
                onChange={(e) => { setSlotService(e.target.value); loadSlots(e.target.value, slotMonth); }}
                className="bg-lake-subtle border hairline-light rounded-md px-4 py-2.5 text-sm outline-none"
              >
                {services.map((s) => (
                  <option key={s.id} value={s.id}>{s.title_it}</option>
                ))}
              </select>
              <div className="flex items-center gap-3">
                <button onClick={() => shiftMonth(-1)} className="border hairline-light rounded-full px-3 py-2 text-xs hover:bg-glacier/10">←</button>
                <span data-testid="slot-month-label" className="font-mono text-sm">{slotMonth}</span>
                <button onClick={() => shiftMonth(1)} className="border hairline-light rounded-full px-3 py-2 text-xs hover:bg-glacier/10">→</button>
              </div>
            </div>

            <form data-testid="add-slot-form" onSubmit={addSlot} className="bg-lake-subtle border hairline-light rounded-md p-5 mb-8 flex flex-wrap items-end gap-4">
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-glacier/50 mb-2">Data</label>
                <input
                  data-testid="add-slot-date"
                  type="date"
                  required
                  value={newSlot.date}
                  onChange={(e) => setNewSlot({ ...newSlot, date: e.target.value })}
                  className="bg-lake-deep border hairline-light rounded-md px-3 py-2.5 text-sm outline-none"
                />
              </div>
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-glacier/50 mb-2">Ora</label>
                <input
                  data-testid="add-slot-time"
                  type="time"
                  required
                  value={newSlot.time}
                  onChange={(e) => setNewSlot({ ...newSlot, time: e.target.value })}
                  className="bg-lake-deep border hairline-light rounded-md px-3 py-2.5 text-sm outline-none"
                />
              </div>
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-glacier/50 mb-2">Posti</label>
                <input
                  data-testid="add-slot-capacity"
                  type="number"
                  min="1"
                  max="30"
                  required
                  value={newSlot.capacity}
                  onChange={(e) => setNewSlot({ ...newSlot, capacity: e.target.value })}
                  className="bg-lake-deep border hairline-light rounded-md px-3 py-2.5 text-sm outline-none w-24"
                />
              </div>
              <button
                data-testid="add-slot-submit"
                type="submit"
                className="bg-gold text-lake-deep font-semibold px-6 py-2.5 rounded-full text-sm hover:bg-glacier transition-colors"
              >
                Aggiungi slot
              </button>
            </form>

            <form data-testid="bulk-slot-form" onSubmit={bulkCreate} className="bg-lake-subtle border hairline-light rounded-md p-5 mb-8">
              <h4 className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-gold mb-4">
                <CalendarPlus className="w-4 h-4" /> Genera disponibilità per un periodo
              </h4>
              <div className="flex flex-wrap items-end gap-4">
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-glacier/50 mb-2">Da</label>
                  <input
                    data-testid="bulk-from"
                    type="date"
                    required
                    value={bulk.from}
                    onChange={(e) => setBulk({ ...bulk, from: e.target.value })}
                    className="bg-lake-deep border hairline-light rounded-md px-3 py-2.5 text-sm outline-none"
                  />
                </div>
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-glacier/50 mb-2">A</label>
                  <input
                    data-testid="bulk-to"
                    type="date"
                    required
                    value={bulk.to}
                    onChange={(e) => setBulk({ ...bulk, to: e.target.value })}
                    className="bg-lake-deep border hairline-light rounded-md px-3 py-2.5 text-sm outline-none"
                  />
                </div>
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-glacier/50 mb-2">Posti per slot</label>
                  <input
                    data-testid="bulk-capacity"
                    type="number"
                    min="1"
                    max="40"
                    required
                    value={bulk.capacity}
                    onChange={(e) => setBulk({ ...bulk, capacity: e.target.value })}
                    className="bg-lake-deep border hairline-light rounded-md px-3 py-2.5 text-sm outline-none w-24"
                  />
                </div>
                <button
                  data-testid="bulk-submit"
                  type="submit"
                  className="bg-gold text-lake-deep font-semibold px-6 py-2.5 rounded-full text-sm hover:bg-glacier transition-colors"
                >
                  Genera slot
                </button>
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-glacier/50 mr-1">Orari:</span>
                {ALL_TIMES.map((tm) => (
                  <button
                    key={tm}
                    type="button"
                    data-testid={`bulk-time-${tm}`}
                    onClick={() =>
                      setBulk({
                        ...bulk,
                        times: bulk.times.includes(tm)
                          ? bulk.times.filter((x) => x !== tm)
                          : [...bulk.times, tm].sort(),
                      })
                    }
                    className={`border rounded-full px-4 py-1.5 font-mono text-xs transition-colors ${
                      bulk.times.includes(tm)
                        ? "bg-teal text-white border-teal"
                        : "hairline-light text-glacier/60 hover:text-glacier"
                    }`}
                  >
                    {tm}
                  </button>
                ))}
              </div>
            </form>

            <div className="space-y-6">
              {Object.keys(groupedSlots).length === 0 && (
                <p className="text-glacier/50 font-mono text-sm py-10 text-center">Nessuno slot in questo mese.</p>
              )}
              {Object.entries(groupedSlots).map(([d, list]) => {
                const allInactive = list.every((s) => !s.active);
                const editing = list.find((x) => x.id === editingSlot);
                return (
                <div key={d}>
                  <div className="flex flex-wrap items-center gap-3 mb-3">
                    <h4 className="font-mono text-xs uppercase tracking-[0.2em] text-gold capitalize">
                      {new Date(d + "T12:00:00").toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}
                    </h4>
                    <button
                      data-testid={`day-toggle-${d}`}
                      onClick={() => toggleDay(d, allInactive)}
                      className={`flex items-center gap-1.5 border rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-wider transition-colors ${
                        allInactive
                          ? "border-teal/50 text-teal-light hover:bg-teal/20"
                          : "border-terracotta/50 text-terracotta hover:bg-terracotta/20"
                      }`}
                    >
                      <Ban className="w-3 h-3" />
                      {allInactive ? "Riattiva giorno" : "Blocca giorno"}
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {list.map((s) => (
                      <div key={s.id} className="flex items-center gap-1">
                        <button
                          data-testid={`slot-toggle-${s.date}-${s.time}`}
                          onClick={() => toggleSlot(s.id, !s.active)}
                          title={s.active ? "Clicca per bloccare (maltempo)" : "Clicca per riattivare"}
                          className={`border rounded-full px-4 py-2 font-mono text-xs transition-colors ${
                            s.active
                              ? "border-teal/50 text-teal-light hover:bg-teal/20"
                              : "border-terracotta/50 text-terracotta line-through hover:bg-terracotta/20"
                          }`}
                        >
                          {s.time} · {s.capacity - s.booked}/{s.capacity}
                        </button>
                        <button
                          data-testid={`slot-edit-${s.date}-${s.time}`}
                          onClick={() => {
                            setEditingSlot(editingSlot === s.id ? null : s.id);
                            setEditForm({ time: s.time, capacity: s.capacity });
                          }}
                          title="Modifica orario e posti"
                          className="w-8 h-8 rounded-full border hairline-light flex items-center justify-center text-glacier/50 hover:text-gold hover:border-gold/50 transition-colors"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                  {editing && (
                    <div className="mt-3 bg-lake-deep border border-gold/30 rounded-md p-4 flex flex-wrap items-end gap-4">
                      <div>
                        <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-glacier/50 mb-2">Ora</label>
                        <input
                          data-testid="edit-slot-time"
                          type="time"
                          value={editForm.time}
                          onChange={(e) => setEditForm({ ...editForm, time: e.target.value })}
                          className="bg-lake-subtle border hairline-light rounded-md px-3 py-2.5 text-sm outline-none"
                        />
                      </div>
                      <div>
                        <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-glacier/50 mb-2">
                          Posti totali (prenotati: {editing.booked})
                        </label>
                        <input
                          data-testid="edit-slot-capacity"
                          type="number"
                          min={editing.booked}
                          max="40"
                          value={editForm.capacity}
                          onChange={(e) => setEditForm({ ...editForm, capacity: e.target.value })}
                          className="bg-lake-subtle border hairline-light rounded-md px-3 py-2.5 text-sm outline-none w-28"
                        />
                      </div>
                      <button
                        data-testid="edit-slot-save"
                        onClick={() => saveSlotEdit(editing.id)}
                        className="flex items-center gap-2 bg-gold text-lake-deep font-semibold px-5 py-2.5 rounded-full text-sm hover:bg-glacier transition-colors"
                      >
                        <Save className="w-4 h-4" /> Salva
                      </button>
                      <button
                        data-testid="edit-slot-delete"
                        onClick={() => deleteSlot(editing.id)}
                        disabled={editing.booked > 0}
                        title={editing.booked > 0 ? "Impossibile eliminare: ci sono prenotazioni attive" : "Elimina slot"}
                        className="flex items-center gap-2 border border-terracotta/50 text-terracotta px-5 py-2.5 rounded-full text-sm hover:bg-terracotta hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <Trash2 className="w-4 h-4" /> Elimina
                      </button>
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          </section>
        )}
      </main>

      {editingBooking && editB && (
        <div
          data-testid="booking-edit-modal"
          className="fixed inset-0 z-[90] bg-lake-deep/85 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setEditingBooking(null)}
        >
          <div
            className="bg-lake-subtle border hairline-light rounded-md w-full max-w-lg max-h-[90vh] overflow-y-auto p-7"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-serif text-2xl">Modifica <span className="font-mono text-gold text-xl">{editingBooking.code}</span></h3>
              <button data-testid="booking-edit-close" onClick={() => setEditingBooking(null)} className="text-glacier/50 hover:text-glacier transition-colors" aria-label="chiudi">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-glacier/50 mb-2">Nome</label>
                  <input
                    data-testid="edit-booking-name"
                    value={editB.name}
                    onChange={(e) => setEditB({ ...editB, name: e.target.value })}
                    className="w-full bg-lake-deep border hairline-light rounded-md px-3 py-2.5 text-sm outline-none focus:border-teal-light"
                  />
                </div>
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-glacier/50 mb-2">Telefono</label>
                  <input
                    data-testid="edit-booking-phone"
                    value={editB.phone}
                    onChange={(e) => setEditB({ ...editB, phone: e.target.value })}
                    className="w-full bg-lake-deep border hairline-light rounded-md px-3 py-2.5 text-sm outline-none focus:border-teal-light"
                  />
                </div>
              </div>
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-glacier/50 mb-2">Email</label>
                <input
                  data-testid="edit-booking-email"
                  type="email"
                  value={editB.email}
                  onChange={(e) => setEditB({ ...editB, email: e.target.value })}
                  className="w-full bg-lake-deep border hairline-light rounded-md px-3 py-2.5 text-sm outline-none focus:border-teal-light"
                />
              </div>
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-glacier/50 mb-2">Appuntamento</label>
                <select
                  data-testid="edit-booking-slot"
                  value={editB.slot_id}
                  onChange={(e) => setEditB({ ...editB, slot_id: e.target.value })}
                  className="w-full bg-lake-deep border hairline-light rounded-md px-3 py-2.5 text-sm outline-none"
                >
                  <option value={editingBooking.slot_id}>
                    Attuale: {editingBooking.date} · {editingBooking.time}
                  </option>
                  {editSlotOptions.filter((o) => o.slot_id !== editingBooking.slot_id).map((o) => (
                    <option key={o.slot_id} value={o.slot_id}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-glacier/50 mb-2">Persone / kayak</label>
                  <input
                    data-testid="edit-booking-participants"
                    type="number"
                    min="1"
                    max="20"
                    value={editB.participants}
                    onChange={(e) => setEditB({ ...editB, participants: e.target.value })}
                    className="w-full bg-lake-deep border hairline-light rounded-md px-3 py-2.5 text-sm outline-none focus:border-teal-light"
                  />
                </div>
                {editingBooking.hours != null && (
                  <div>
                    <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-glacier/50 mb-2">Durata</label>
                    <select
                      data-testid="edit-booking-hours"
                      value={editB.hours}
                      onChange={(e) => setEditB({ ...editB, hours: e.target.value })}
                      className="w-full bg-lake-deep border hairline-light rounded-md px-3 py-2.5 text-sm outline-none"
                    >
                      {[1, 2, 3].map((h) => <option key={h} value={h}>{h}h</option>)}
                    </select>
                  </div>
                )}
              </div>
              <label className="flex items-center gap-3 cursor-pointer select-none">
                <input
                  data-testid="edit-booking-resident"
                  type="checkbox"
                  checked={editB.is_resident}
                  onChange={(e) => setEditB({ ...editB, is_resident: e.target.checked })}
                  className="w-4 h-4 accent-[#E5A93B]"
                />
                <span className="text-sm text-glacier/75">Residente a Manerba del Garda (−20%)</span>
              </label>
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-glacier/50 mb-2">Note</label>
                <textarea
                  data-testid="edit-booking-notes"
                  rows={2}
                  value={editB.notes}
                  onChange={(e) => setEditB({ ...editB, notes: e.target.value })}
                  className="w-full bg-lake-deep border hairline-light rounded-md px-3 py-2.5 text-sm outline-none focus:border-teal-light resize-none"
                />
              </div>
            </div>
            <p className="mt-4 font-mono text-[11px] text-glacier/45">
              Il totale da pagare al circolo viene ricalcolato automaticamente.
            </p>
            <div className="mt-6 flex gap-3">
              <button
                data-testid="booking-edit-save"
                onClick={saveBookingEdit}
                className="flex-1 flex items-center justify-center gap-2 bg-gold text-lake-deep font-semibold py-3 rounded-full text-sm hover:bg-glacier transition-colors"
              >
                <Save className="w-4 h-4" /> Salva modifiche
              </button>
              <button
                data-testid="booking-edit-cancel"
                onClick={() => setEditingBooking(null)}
                className="border hairline-light text-glacier/70 px-6 py-3 rounded-full text-sm hover:bg-glacier/10 transition-colors"
              >
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
